import { useEffect, useRef, useState } from 'react';
import {
  lmsApi,
  type StudentSummary,
  type TaskAttachment,
  type TaskInput,
  type TeacherTask,
} from '../lib/lmsApi';
import { Icon } from '../ui/icons';
import { fromLocalInput, toLocalInput } from './dates';

export interface TaskEditorProps {
  /** `null` means "new task". */
  task: TeacherTask | null;
  students: StudentSummary[];
  /** Preselected when the teacher started from a student's card. */
  initialStudentIds?: string[];
  /** Subjects already used in this organisation, offered as suggestions. */
  knownSubjects?: string[];
  /** Shown when there is nobody to assign to; differs for a teacher and the owner. */
  noStudentsHint?: string;
  onSave: (input: TaskInput, taskId: string | null) => Promise<void>;
  onDelete: (taskId: string) => Promise<void>;
  onClose: () => void;
}

/**
 * Create/edit form for a task.
 *
 * Students are picked here, per task, rather than belonging to a saved group:
 * the same homework goes to "these four today" and "the whole class next
 * week", and maintaining group objects for that would be bookkeeping the
 * teacher never asked for.
 */
export function TaskEditor({
  task,
  students,
  initialStudentIds,
  knownSubjects = [],
  noStudentsHint = 'За вами пока не закреплены ученики — это делает владелец организации.',
  onSave,
  onDelete,
  onClose,
}: TaskEditorProps) {
  const [subject, setSubject] = useState(task?.subject ?? '');
  const [title, setTitle] = useState(task?.title ?? '');
  const [description, setDescription] = useState(task?.description ?? '');
  const [dueAt, setDueAt] = useState<string | null>(task?.dueAt ?? null);
  const [picked, setPicked] = useState<string[]>(
    task?.assignments.map((a) => a.userId) ?? initialStudentIds ?? [],
  );
  const [attachments, setAttachments] = useState<TaskAttachment[]>(task?.attachments ?? []);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Set synchronously, so a double click cannot create the task twice. */
  const inFlight = useRef(false);

  const dialogRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const toggleStudent = (userId: string) => {
    setPicked((current) =>
      current.includes(userId) ? current.filter((id) => id !== userId) : [...current, userId],
    );
  };

  const upload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of files) {
        const uploaded = await lmsApi.uploadTaskAttachment(file);
        setAttachments((current) => [...current, uploaded]);
      }
    } catch {
      setError('Не удалось загрузить файл');
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    // Saving mid-upload would silently drop the file still on its way.
    if (inFlight.current || uploading) return;
    if (picked.length === 0) {
      setError('Выберите хотя бы одного ученика');
      return;
    }
    inFlight.current = true;
    setSaving(true);
    setError(null);
    try {
      await onSave(
        {
          subject: subject.trim(),
          title: title.trim() || 'Без названия',
          description,
          dueAt,
          studentIds: picked,
          attachments,
        },
        task?.id ?? null,
      );
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить');
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };

  return (
    <div
      className="modal-backdrop"
      onPointerDown={(e) => {
        if (!dialogRef.current?.contains(e.target as Node)) onClose();
      }}
    >
      <div
        className="event-editor"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={task ? 'Изменить задачу' : 'Новая задача'}
      >
        <header className="event-editor-head">
          <input
            ref={titleRef}
            className="event-title-input"
            placeholder="Тема задания"
            value={title}
            maxLength={160}
            onChange={(e) => setTitle(e.target.value)}
          />
          <button type="button" className="icon-btn" aria-label="Закрыть" onClick={onClose}>
            <Icon name="close" size={16} />
          </button>
        </header>

        <div className="event-editor-body">
          <div className="field-row">
            <label className="field">
              <span>Предмет</span>
              {/* Free text with suggestions rather than a fixed list: every
                  school's subjects differ, and typing one is faster than
                  maintaining a dictionary of them. */}
              <input
                list="task-subjects"
                placeholder="Алгебра"
                value={subject}
                maxLength={60}
                onChange={(e) => setSubject(e.target.value)}
              />
              <datalist id="task-subjects">
                {knownSubjects.map((name) => (
                  <option key={name} value={name} />
                ))}
              </datalist>
            </label>

            <label className="field">
              <span>Срок</span>
              <input
                type="datetime-local"
                value={dueAt ? toLocalInput(dueAt) : ''}
                onChange={(e) => setDueAt(e.target.value ? fromLocalInput(e.target.value) : null)}
              />
            </label>
          </div>

          <label className="field">
            <span>Описание</span>
            <textarea
              rows={5}
              placeholder="Что сделать, на что обратить внимание…"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>

          <div className="field">
            <span>
              Кому
              {picked.length > 0 && <em className="field-hint"> выбрано {picked.length}</em>}
            </span>
            {students.length === 0 ? (
              <p className="org-members-hint">{noStudentsHint}</p>
            ) : (
              <ul className="student-picker">
                {students.map((student) => (
                  <li key={student.userId}>
                    <label>
                      <input
                        type="checkbox"
                        checked={picked.includes(student.userId)}
                        onChange={() => toggleStudent(student.userId)}
                      />
                      <span className="org-member-avatar" style={{ background: student.color }}>
                        {student.name.slice(0, 1).toUpperCase()}
                      </span>
                      {student.name}
                    </label>
                  </li>
                ))}
              </ul>
            )}
            {students.length > 1 && (
              <div className="student-picker-actions">
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={() => setPicked(students.map((s) => s.userId))}
                >
                  Выбрать всех
                </button>
                <button type="button" className="ghost-btn" onClick={() => setPicked([])}>
                  Снять выбор
                </button>
              </div>
            )}
          </div>

          <div className="field">
            <span>
              Файлы и фото
              {attachments.length > 0 && <em className="field-hint"> {attachments.length} шт.</em>}
            </span>
            <ul className="attachment-list">
              {attachments.map((file) => (
                <li key={file.id}>
                  <a href={file.url} target="_blank" rel="noreferrer">
                    {file.mime.startsWith('image/') ? (
                      <img className="attachment-thumb" src={file.url} alt="" loading="lazy" />
                    ) : (
                      <Icon name="paperclip" size={13} />
                    )}
                    {file.name}
                  </a>
                  <button
                    type="button"
                    aria-label={`Убрать ${file.name}`}
                    onClick={() =>
                      setAttachments((current) => current.filter((f) => f.id !== file.id))
                    }
                  >
                    <Icon name="close" size={13} />
                  </button>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="ghost-btn"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
            >
              <Icon name="paperclip" size={15} />
              {uploading ? 'Загрузка…' : 'Прикрепить'}
            </button>
            <input
              ref={fileRef}
              type="file"
              multiple
              hidden
              onChange={async (e) => {
                const files = e.target.files;
                e.target.value = '';
                await upload(files);
              }}
            />
          </div>

          {error && <p className="form-error">{error}</p>}
        </div>

        <footer className="event-editor-foot">
          {task && (
            <button
              type="button"
              className="ghost-btn is-danger"
              onClick={async () => {
                if (
                  !window.confirm(
                    `Удалить задание «${task.title}»? Решения учеников по нему тоже удалятся. Это нельзя отменить.`,
                  )
                ) {
                  return;
                }
                await onDelete(task.id);
                onClose();
              }}
            >
              <Icon name="trash" size={15} />
              Удалить
            </button>
          )}
          <span className="spacer" />
          <button type="button" className="ghost-btn" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="primary-btn"
            disabled={saving || uploading}
            title={uploading ? 'Дождитесь окончания загрузки файлов' : undefined}
            onClick={submit}
          >
            <Icon name="check" size={15} />
            {saving ? 'Сохранение…' : 'Сохранить'}
          </button>
        </footer>
      </div>
    </div>
  );
}
